// **Redux Imports
import { createSlice } from '@reduxjs/toolkit'

// **Utils Imports
import { APP_MODE, ROLES } from '../../../utils/constant'
import {
  getAppMode,
  getJWTToken,
  getUserDetails,
  removeJWTToken,
  setAppModeInStorage,
  setJWTToken
} from '../../../utils/utils'

const token = getJWTToken()
const userData = getUserDetails()
const appMode = getAppMode()

export const authSlice = createSlice({
  name: 'auth',
  initialState: {
    token,
    appMode:
      userData?.roles === ROLES.LEARNER?.toUpperCase()
        ? APP_MODE.LEARNER
        : appMode,
    userData,
    profile: {},
    role: userData?.roles,
    isAuthenticated: !!userData,
    currentlyActivePlan: null,
    subscriptions: [],
    creditsDetails: null,
    tenantList: [],
    showWelcomeOffer: false,
    ipLocation: null,
    productTourConfig: {
      hasCompletedInitialSetup: false,
      userTourProgress: {}
    }
  },
  reducers: {
    setUserRole(state, action) {
      state.role = action.payload
    },
    setAppMode(state, action) {
      setAppModeInStorage(action.payload)
      state.appMode = action.payload
    },
    setCurrentlyActivePlan(state, action) {
      state.currentlyActivePlan = action.payload
    },
    setAvailableCreditsDetails(state, action) {
      state.creditsDetails = action.payload
    },
    setSubscriptions(state, action) {
      state.subscriptions = action.payload
    },
    setToken(state, action) {
      state.token = action.payload
      state.isAuthenticated = true
    },
    setIPLocation(state, action) {
      state.ipLocation = action.payload
    },
    resetIPLocation(state, action) {
      state.ipLocation = null
    },
    logout(state) {
      removeJWTToken()
      state.token = null
      state.userData = null
      state.isAuthenticated = false
      state.currentlyActivePlan = null
      state.showWelcomeOffer = false
      state.productTourConfig = {
        hasCompletedInitialSetup: false,
        userTourProgress: {}
      }
      sessionStorage.removeItem('lowCreditShown')
    },
    setUserInformation(state, action) {
      try {
        const jwt = action.payload.jwt
        setJWTToken(jwt)

        const userData = getUserDetails()

        if (userData?.roles?.toLowerCase() === ROLES.LEARNER) {
          setAppModeInStorage(APP_MODE.LEARNER)
          state.appMode = APP_MODE.LEARNER
        } else {
          setAppModeInStorage(APP_MODE.HIRING)
          state.appMode = APP_MODE.HIRING
        }

        if (userData) {
          state.token = jwt
          state.role = userData?.roles
          state.userData = userData
          state.isAuthenticated = true
        }
      } catch (e) {}
    },
    setProfileDetails(state, action) {
      const { name = '' } = action.payload || {}
      if (name) {
        state.userData.name = name
      }
      state.profile = action.payload
    },
    setTenantList(state, action) {
      state.tenantList = action.payload
    },
    setHasInitialSetup(state, action) {
      state.productTourConfig.hasCompletedInitialSetup = action.payload
    },
    setUserTourProgress(state, { payload = [] }) {
      const updatedPayload = payload.reduce((acc, curr) => {
        const { pageName, ...rest } = curr
        acc[curr.pageName] = {
          ...rest
        }
        return acc
      }, {})
      state.productTourConfig.userTourProgress = updatedPayload
    },
    updateUserTourProgress(state, { payload }) {
      const { pageName, ...rest } = payload
      const existingProgress =
        state.productTourConfig.userTourProgress[pageName] || {}

      state.productTourConfig.userTourProgress[pageName] = {
        ...existingProgress,
        ...rest
      }
    },
    setShowWelcomeOffer(state, action) {
      state.showWelcomeOffer = action.payload
    }
  }
})

export const {
  logout,
  setToken,
  setAppMode,
  setUserRole,
  setUserInformation,
  setCurrentlyActivePlan,
  setAvailableCreditsDetails,
  setSubscriptions,
  setProfileDetails,
  setTenantList,
  setHasInitialSetup,
  setUserTourProgress,
  updateUserTourProgress,
  setShowWelcomeOffer,
  setIPLocation,
  resetIPLocation
} = authSlice.actions

export default authSlice.reducer
